import React, { createContext, useContext, useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useSchool } from './SchoolContext';
import { Subscription, SubscriptionPlan, SUBSCRIPTION_PLANS } from '../types/subscription';
import toast from 'react-hot-toast';
import { getStripePublishableKey } from '../config/stripe';

const stripePromise = loadStripe(getStripePublishableKey());

interface SubscriptionContextType {
  subscription: Subscription | null;
  currentPlan: SubscriptionPlan | null;
  loading: boolean;
  upgradeToPlan: (planId: string) => Promise<void>;
  downgradeToPlan: (planId: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  addAddOn: (addOnId: string) => Promise<void>;
  removeAddOn: (addOnId: string) => Promise<void>;
  hasFeature: (feature: string) => boolean;
  canUseFeature: (feature: string) => boolean;
  getUserLimit: () => number;
  isWithinUserLimit: (currentUsers: number) => boolean;
  manageSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { selectedSchool: school } = useSchool();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!school?.id) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    console.log('🔄 SubscriptionContext: Loading subscription for school:', school.id);

    // Listen to the school document directly since subscription is stored there
    const schoolRef = doc(db, 'schools', school.id);
    const unsubscribe = onSnapshot(schoolRef, (snapshot) => {
      if (!snapshot.exists()) {
        console.log('❌ SubscriptionContext: School document not found');
        setSubscription(null);
        setLoading(false);
        return;
      }

      const schoolData = snapshot.data();
      const subscriptionData = schoolData.subscription;

      console.log('📋 SubscriptionContext: School data received:', {
        hasSubscription: !!subscriptionData,
        subscriptionStatus: subscriptionData?.status,
        subscriptionPlan: subscriptionData?.planId
      });

      if (!subscriptionData || !subscriptionData.status || subscriptionData.status === 'free') {
        console.log('📋 SubscriptionContext: No active subscription found, using free plan');
        setSubscription(null);
      } else {
        console.log('✅ SubscriptionContext: Active subscription found:', subscriptionData.planId);
        
        // Convert Firestore timestamps to Date objects safely
        const subscription: Subscription = {
          id: school.id, // Use school ID as subscription ID
          schoolId: school.id,
          planId: subscriptionData.planId || 'free',
          status: subscriptionData.status,
          userCount: subscriptionData.userCount || 5,
          amount: subscriptionData.amount || 0,
          currency: subscriptionData.currency || 'usd',
          stripeSubscriptionId: subscriptionData.stripeSubscriptionId,
          stripeCustomerId: subscriptionData.stripeCustomerId,
          currentPeriodStart: subscriptionData.currentPeriodStart?.toDate() || new Date(),
          currentPeriodEnd: subscriptionData.currentPeriodEnd?.toDate() || new Date(),
          createdAt: subscriptionData.activatedAt?.toDate() || new Date(),
          updatedAt: subscriptionData.updatedAt?.toDate() || new Date(),
        };

        setSubscription(subscription);
      }
      setLoading(false);
    }, (error) => {
      console.error('❌ SubscriptionContext: Error listening to school document:', error);
      setSubscription(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [school?.id]);

  const currentPlan = subscription 
    ? SUBSCRIPTION_PLANS.find(plan => plan.id === subscription.planId) || SUBSCRIPTION_PLANS[0]
    : SUBSCRIPTION_PLANS[0]; // Default to free plan

  const upgradeToPlan = async (planId: string) => {
    if (!school?.id || !user) return;

    try {
      setLoading(true);
      
      // Redirect to billing page to handle upgrade via UpgradeModal
      window.location.href = '/billing';
      
      toast.info('Redirecting to billing page to upgrade your plan...');
    } catch (error) {
      console.error('Error upgrading subscription:', error);
      toast.error('Failed to upgrade subscription');
    } finally {
      setLoading(false);
    }
  };

  const downgradeToPlan = async (planId: string) => {
    if (!school?.id || !subscription) return;

    try {
      setLoading(true);
      
      // Note: Plan changes require new Stripe checkout session
      // Direct subscription modifications require server-side Stripe API calls
      toast.info('Please contact support to change your plan or cancel and resubscribe.');
      
    } catch (error) {
      console.error('Error downgrading subscription:', error);
      toast.error('Failed to downgrade subscription');
    } finally {
      setLoading(false);
    }
  };

  const cancelSubscription = async () => {
    if (!subscription || !school) return;

    try {
      setLoading(true);
      
      // Get the stored customer ID from school subscription data
      const schoolDoc = await getDoc(doc(db, 'schools', school.id));
      const subscriptionData = schoolDoc.data()?.subscription;
      
      if (subscriptionData?.stripeCustomerId) {
        // Redirect to Stripe Customer Portal
        // Note: In production, you'd create a portal session server-side for security
        // This is a simplified approach for client-side only implementation
        
        // For Stripe Test mode, the portal URL follows this pattern
        // Real customer IDs would need proper portal session creation
        const returnUrl = `${window.location.origin}/portal/return`;
        const portalUrl = `https://billing.stripe.com/p/login/test_${btoa(subscriptionData.stripeCustomerId)}?return_url=${encodeURIComponent(returnUrl)}`;
        
        window.location.href = portalUrl;
        toast.info('Redirecting to Stripe billing portal...');
      } else {
        // Fallback for older subscriptions without customer ID
        toast.info('Please contact support at support@schoolsync.ai to manage your subscription.');
      }
      
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      toast.error('Failed to access billing portal');
    } finally {
      setLoading(false);
    }
  };

  const reactivateSubscription = async () => {
    if (!subscription) return;

    try {
      setLoading(true);
      
      // Reactivation requires creating a new subscription
      window.location.href = '/billing';
      toast.info('Please create a new subscription to reactivate.');
      
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      toast.error('Failed to reactivate subscription');
    } finally {
      setLoading(false);
    }
  };

  const addAddOn = async (addOnId: string) => {
    if (!subscription) return;

    try {
      setLoading(true);
      
      // Add-ons are handled through the upgrade modal
      window.location.href = '/billing';
      toast.info('Please use the billing page to manage add-ons.');
      
    } catch (error) {
      console.error('Error adding add-on:', error);
      toast.error('Failed to add add-on');
    } finally {
      setLoading(false);
    }
  };

  const removeAddOn = async (addOnId: string) => {
    if (!subscription) return;

    try {
      setLoading(true);
      
      // Add-on management requires server-side API calls
      toast.info('Please contact support to remove add-ons from your subscription.');
      
    } catch (error) {
      console.error('Error removing add-on:', error);
      toast.error('Failed to remove add-on');
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (feature: string): boolean => {
    if (!currentPlan) return false;
    return currentPlan.features.includes(feature);
  };

  const canUseFeature = (feature: string): boolean => {
    if (!subscription && currentPlan?.id === 'free') {
      return currentPlan.features.includes(feature);
    }
    
    if (!subscription) return false;
    
    return subscription.status === 'active' && hasFeature(feature);
  };

  const getUserLimit = (): number => {
    if (!currentPlan) return 0;
    return currentPlan.maxUsers || Infinity;
  };

  const isWithinUserLimit = (currentUsers: number): boolean => {
    const limit = getUserLimit();
    return currentUsers <= limit;
  };

  const manageSubscription = async () => {
    if (!school) return;

    try {
      setLoading(true);
      
      // Get the stored customer ID from school subscription data
      const schoolDoc = await getDoc(doc(db, 'schools', school.id));
      const subscriptionData = schoolDoc.data()?.subscription;
      
      if (subscriptionData?.stripeCustomerId) {
        console.log('🔄 Creating Stripe Customer Portal session for:', subscriptionData.stripeCustomerId);
        
        // Create portal session via Cloud Function (proper Stripe integration)
        const response = await fetch('https://us-central1-schoolsync-bfccc.cloudfunctions.net/createPortalSession', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            customerId: subscriptionData.stripeCustomerId,
            returnUrl: `${window.location.origin}/billing`
          })
        });

        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }
        
        if (data.url) {
          console.log('✅ Portal session created, redirecting to:', data.url);
          toast.info('Redirecting to Stripe billing portal...');
          window.location.href = data.url;
        } else {
          throw new Error('No portal URL received');
        }
      } else if (subscription) {
        // If we have a subscription but no customer ID, redirect to billing page
        console.log('❌ No customer ID found in subscription data');
        window.location.href = '/billing';
        toast.info('Redirecting to billing page...');
      } else {
        // No subscription, redirect to upgrade
        window.location.href = '/billing';
        toast.info('Redirecting to upgrade page...');
      }
      
    } catch (error) {
      console.error('❌ Error accessing billing portal:', error);
      toast.error('Failed to access billing portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const value: SubscriptionContextType = {
    subscription,
    currentPlan,
    loading,
    upgradeToPlan,
    downgradeToPlan,
    cancelSubscription,
    reactivateSubscription,
    addAddOn,
    removeAddOn,
    hasFeature,
    canUseFeature,
    getUserLimit,
    isWithinUserLimit,
    manageSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      <Elements stripe={stripePromise}>
        {children}
      </Elements>
    </SubscriptionContext.Provider>
  );
};